import * as React from 'react';

import {
  enumUnit,
  Unit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import {
  enumLoadWeightType,
  InventoryItemRecord,
  LoadFormLineItem,
} from '@rematter/modules/trade/types/load';

type Props = {
  stagedLineItems?: LoadFormLineItem[];
  requestedLineItems?: LoadFormLineItem[];
  weightDisplayUnitPreference?: WeightDisplayUnit;
  inventoryItemsById?: Record<string, InventoryItemRecord>;
  onUpdateItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
};

const createEachPricingUpdate = (item: LoadFormLineItem) => ({
  pricingUnit: 'Each',
  weights: {
    ...item.weights,
    [enumLoadWeightType.Tare]: {
      value: 0,
      unit: enumUnit.Each,
      __typename: 'Weight',
    },
    [enumLoadWeightType.Net]: {
      value: item.weights?.[enumLoadWeightType.Gross]?.value,
      unit: enumUnit.Each,
      __typename: 'Weight',
    },
  },
});

export const usePricingUnitSync = ({
  stagedLineItems,
  requestedLineItems,
  weightDisplayUnitPreference,
  inventoryItemsById,
  onUpdateItem,
}: Props) => {
  const requestedItemsMap = React.useMemo(
    () => new Map(requestedLineItems?.map((item) => [item.itemId, item])),
    [requestedLineItems],
  );

  React.useEffect(() => {
    stagedLineItems?.forEach((item) => {
      const matchingRequestedItem = item.itemId
        ? requestedItemsMap.get(item.itemId)
        : null;

      const shouldUseEachPricing =
        matchingRequestedItem?.pricingUnit === 'Each' &&
        weightDisplayUnitPreference === 'PriceUnit';

      if (shouldUseEachPricing && item.pricingUnit !== 'Each') {
        onUpdateItem(item.id, createEachPricingUpdate(item));
      } else if (
        !shouldUseEachPricing &&
        item.pricingUnit === 'Each' &&
        matchingRequestedItem?.pricingUnit === 'Each'
      ) {
        const effectiveUnit = item.itemId
          ? ((inventoryItemsById?.[item.itemId]
              ?.effectivePricingUnit as Unit) ?? enumUnit.Pounds)
          : enumUnit.Pounds;
        onUpdateItem(item.id, { pricingUnit: effectiveUnit });
      }
    });
  }, [
    stagedLineItems,
    requestedItemsMap,
    weightDisplayUnitPreference,
    inventoryItemsById,
    onUpdateItem,
  ]);
};
